import { isPlatformBrowser, LOCATION_INITIALIZED } from '@angular/common';
import { HttpClient, provideHttpClient, withFetch, withInterceptorsFromDi } from '@angular/common/http';
import { APP_ID, APP_INITIALIZER, CSP_NONCE, ErrorHandler, inject, Injector, NgModule, Optional, PLATFORM_ID, TransferState } from '@angular/core';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { NgxPageScrollModule } from 'ngx-page-scroll';
import { NgxPageScrollCoreModule } from 'ngx-page-scroll-core';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { GlobalModule } from '@techmore/global';
// import { LoadingModule } from '@techmore/loading';
import { provideLuxonDateAdapter } from '@angular/material-luxon-adapter';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { UrlSerializer } from '@angular/router';
import { Response } from 'express';
import { Subject } from 'rxjs';
import { environment } from 'src/environments/environment';
import { INTERNAL_KEY, RESPONSE } from '../express.tokens';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { HtmlSuffixURLSerializer } from './share/entity/html-suffix-url-serializer';
import { BackStageTemplateService } from './share/service/back-stage-template.service';
import { GlobalErrorHandlerService } from './share/service/global-error-handler.service';
import { LoggerService } from './share/service/logger.service';
import { ServerConfigService } from './share/service/server-config.service';
import { translateBrowserLoaderFactory } from './share/service/translate-browser-loader.service';
import { CSP_NONCE_KEY } from './share/tokens/csp-nonce.statekey';
import { GENERATOR_ENV_TOKEN, GeneratorEnv } from './share/tokens/generator-env.token';
import { IS_BROWSER_TOKEN } from './share/tokens/is-browser.token';
import { BackStagePayload } from './web-generator/template-generator/entity/back-stage-payload';
import { provideClientHydration, withEventReplay, withNoHttpTransferCache } from '@angular/platform-browser';
import { REQUEST_MARK_TOKEN } from './share/tokens/request-mark.token';
import { REQUEST_MARK_KEY } from './share/tokens/request-mark.statekey';
import { initializationFactory } from './initializationFactory.fn';
import { IS_INTERNAL_KEY } from './share/tokens/is-internal.statekey';


// export function initializationFactory(injector: Injector, serverConfigService: ServerConfigService, loggerService: LoggerService) {
//   return () => new Promise<any>((resolve: any) => {
//     const locationInitialized = injector.get(LOCATION_INITIALIZED, Promise.resolve(null));
//     const platformId = injector.get(PLATFORM_ID);
//     const isBrowser = isPlatformBrowser(platformId);
//     if (!isBrowser) {
//       loggerService.info(`initializationFactory`);
//     }
//     serverConfigService = serverConfigService ?? injector.get(ServerConfigService);
//     locationInitialized.then(() => {
//       if (!isBrowser) {
//         loggerService.info(`locationInitialized`);
//       }
//       let backStageSubj: Subject<BackStagePayload>;
//       if (isBrowser) {
//         const url = serverConfigService.url;
//         if (serverConfigService.isEditModeUrl) { // 'aHR0cCUzQSUyRiUyRmxvY2FsaG9zdCUzQTQzMDA='
//           // 如果是編輯模式的 url，直接進 CSR 畫面

//           const queryParamSatrtIndex = url.indexOf('?');
//           const url2 = url.slice(0, queryParamSatrtIndex);
//           let stageUrl: string;
//           let match: any;
//           try {
//             stageUrl = decodeURIComponent(atob(decodeURIComponent(serverConfigService.queryParams['stage'])));
//             match = environment.allowBackStageDomain.find(domain => domain === stageUrl);
//           } catch (e) {
//             console.error(e);
//           }
//           const opener = window.opener;
//           if (environment.production) {
//             if (!opener || opener === window || !match || !stageUrl) {
//               if (!opener) {
//                 console.error('no opener');
//               } else if (opener === window) {
//                 console.error('open self');
//               } else if (!match) {
//                 console.error('allowBackStageDomain not match');
//               }
//               alert('請從後台進入!');
//               window.location.href = url2;
//             }
//           }

//           const urlSegs = url2.replace(/https?:\/\//g, '').split('/');
//           const isIndexPage = urlSegs.length === 2;
//           const isSubjectDetailPage = urlSegs.length === 4;
//           window.opener.postMessage('appInit', stageUrl);
//           const backStageTemplateService = injector.get(BackStageTemplateService);
//           backStageSubj = backStageTemplateService.listenOnWindowMessage(isIndexPage, isSubjectDetailPage);
//         }
//       }

//       serverConfigService.getConfig(backStageSubj).toPromise().then((config) => resolve(config));
//     });
//   });
// }

export const DATE_FORMATS = {
  parse: {
    dateInput: 'yyyy/MM/dd',
  },
  display: {
    dateInput: 'yyyy/MM/dd',
    monthYearLabel: 'yyyy MMMM',
    dateA11yLabel: 'DDD',
    monthYearA11yLabel: 'yyyy MMMM',
  },
};

@NgModule({
  declarations: [
    AppComponent,
  ],
  imports: [
    AppRoutingModule,
    BrowserAnimationsModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: translateBrowserLoaderFactory,
        deps: [HttpClient, TransferState]
      },
      defaultLanguage: 'zh-tw',
    }),
    NgxPageScrollCoreModule,
    NgxPageScrollModule,
    GlobalModule.forRoot(),
  ],
  providers: [
    { provide: APP_ID, useValue: 'serverApp' },
    {
      provide: GENERATOR_ENV_TOKEN, useFactory: () => {
        return Object.assign(new GeneratorEnv(), {
          apiHost: inject(IS_BROWSER_TOKEN) ? environment.ipApiHost : environment.apiHost,
          editApiHost: environment.editApiHost,
          allowBackStageDomain: environment.allowBackStageDomain,
          recaptchaKey: environment.recaptchaKey,
          commonToken: environment.commonToken,
          accessTokenKey: 'accessToken',
          refreshTokenKey: 'refreshToken',
          googleSiteVerification: environment.googleSiteVerification,
        }) as GeneratorEnv;
      }, multi: false
    },
    { provide: APP_INITIALIZER, useFactory: initializationFactory, deps: [Injector], multi: true },
    {
      provide: CSP_NONCE, deps: [TransferState, [new Optional(), RESPONSE]], useFactory: (tState: TransferState, response: Response) => {
        if (inject(IS_BROWSER_TOKEN)) {
          return tState.get(CSP_NONCE_KEY, '');
        }
        return response.locals['cspNonce'];
      }
    },
    {
      provide: REQUEST_MARK_TOKEN, deps: [PLATFORM_ID, TransferState, [new Optional(), RESPONSE]], useFactory: (platformId: object, tState: TransferState, response: Response) => {
        if (isPlatformBrowser(platformId)) {
          return tState.get(REQUEST_MARK_KEY, '');
        }
        return response?.locals?.['requestMark'] ?? '';
      }
    },
    { provide: MAT_DATE_LOCALE, useValue: 'zh-TW' },
    provideLuxonDateAdapter(DATE_FORMATS),
    { provide: UrlSerializer, useClass: HtmlSuffixURLSerializer },
    { provide: ErrorHandler, useClass: GlobalErrorHandlerService },
    provideHttpClient(withInterceptorsFromDi(), withFetch()),
    provideAnimationsAsync(),
    provideClientHydration(
      withEventReplay(),
      withNoHttpTransferCache(),
    ),
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }
